/**
 * Body scroll/selection lock — shared, owner-keyed.
 *
 * Several surfaces need to freeze the page behind them (`document.body.style
 * .overflow = 'hidden'`) and stop the background from being text-selected
 * (`userSelect = 'none'`). Because those are two *global* styles, every writer
 * that sets and clears them directly ends up clobbering the others: whichever
 * surface hides last wins, and its unconditional teardown releases a lock a
 * still-open surface underneath continues to require.
 *
 * Concretely (the bugs this module fixes):
 * - `AddContentSheet.show` can raise the budget-exceeded overlay from *inside*
 *   an already-open `SelectContentTypeSheet` and return without opening a
 *   nested sheet. Dismissing the overlay then ran `AppOverlayHost`'s effect
 *   cleanup, which cleared both styles even though the outer sheet was still up
 *   — the page scrolled and stayed selectable behind it. `ExpauseSheetProvider`'s
 *   effect does not re-run in that situation, so nothing restored the lock.
 * - The account-suspended overlay is raised on `/login` (`LoginPage` →
 *   `onSuspended`), so `AppOverlayHost`'s lock effect fires *before* the app
 *   shell mounts on `/`. `ExpandableTopBar`'s collapsed-state effect then ran on
 *   mount and cleared the very lock the terminal overlay had just taken — the
 *   feed scrolled and stayed selectable behind an un-dismissible overlay for the
 *   whole first (and usually only) raise, because `AppOverlayHost`'s effect
 *   never re-runs while its dep is unchanged.
 *
 * The fix is to make the lock a *reference count keyed by owner*: each surface
 * declares itself locked or unlocked under a stable owner id, and the styles
 * reflect whether **any** owner still holds the lock. Calls are idempotent, so
 * this tolerates every call pattern in use — `AppOverlayHost`'s
 * acquire/release-on-unmount effect, `ExpauseSheet`'s repeated set-or-clear on
 * every sheet-stack change, and `ExpandableTopBar`'s set-to-current-state on
 * every expand/route change.
 *
 * Known owners (this module is the shared owner of `overflow` / `userSelect`
 * for exactly these call sites):
 * - `'appOverlayHost'`      — `presentation/components/app/overlays/AppOverlayHost.tsx`
 * - `'expauseSheet'`        — `presentation/components/common/sheet/service/sheetImpl/ExpauseSheet.tsx`
 * - `'expandableTopBar:<instance>'` — `presentation/components/common/appBar/expandableTopBar/ExpandableTopBar.tsx`
 *   (per-instance id: the top bar lives inside a route-cached layout, so more
 *   than one instance is mounted at a time and a shared id would let a hidden
 *   instance release the visible one's lock)
 *
 * Not migrated: `ContentComponentFullScreenSupport.tsx` also writes these
 * globals directly. It guards its restore with `scrollLockAppliedRef` so it
 * never clears a lock it did not apply, and it deliberately does not take the
 * `userSelect` half — migrating it would newly disable text selection during
 * fullscreen video. Its `overflow` claim is still clobberable by a release here
 * (unchanged by this module); bring it in only together with that decision.
 *
 * SSR-defensive: the owner bookkeeping always runs, the DOM write is skipped
 * when `document` is undefined (matching the other `presentation/utils`
 * helpers).
 */

/** Extra globals a lock owner can opt into on top of the base lock. */
export interface BodyScrollLockOptions {
  /**
   * Also hand the locking surface the whole viewport: `overflow: hidden` on
   * `<html>` as well as `<body>`, `overscroll-behavior: contain` on both (no
   * scroll chaining or pull-to-refresh behind the surface), and
   * `scrollbar-gutter: auto` on both — releasing the `stable` gutter
   * `index.css` reserves so a full-bleed surface can use the full width.
   *
   * Opt-in rather than always-on because it is *layout-visible*: applying it
   * for a sheet or a modal would shift the page by the gutter width every time
   * one opens. Only surfaces that already cover the viewport ask for it.
   *
   * Reference-counted like the base lock: the five properties are applied while
   * **any** owner asks for them and cleared when the last such owner releases.
   */
  fullViewport?: boolean;
}

/** Owners currently holding the lock. Empty ⇒ the page is free to scroll. */
const owners = new Map<string, BodyScrollLockOptions>();

/**
 * Whether this module currently owns the `fullViewport` properties.
 *
 * They are only cleared when *this module* applied them, so a release by a
 * base-lock-only owner (a sheet closing) can never wipe the full-viewport
 * styles the un-migrated direct writer above holds.
 */
let fullViewportApplied = false;

/**
 * Acquire or release the body scroll/selection lock for one owner.
 *
 * Idempotent in both directions: locking twice under the same owner holds a
 * single claim, and releasing an owner that never locked is a no-op. The
 * styles are (re)applied from the resulting owner set on every call, so a
 * release by one owner can never drop another owner's still-required lock.
 *
 * @param owner - Stable id of the calling surface (see the known owners in the
 * module header). Each surface must use the same id for its lock and unlock.
 * @param locked - `true` to hold the lock, `false` to release it.
 * @param options - Extra globals this owner claims while locked. Ignored when
 * releasing (the owner's whole claim goes away).
 */
export function setBodyScrollLock(
  owner: string,
  locked: boolean,
  options: BodyScrollLockOptions = {},
): void {
  if (locked) {
    owners.set(owner, options);
  } else {
    owners.delete(owner);
  }

  if (typeof document === 'undefined') {
    return;
  }

  const { documentElement, body } = document;

  const isLocked = owners.size > 0;
  body.style.overflow = isLocked ? 'hidden' : '';
  body.style.userSelect = isLocked ? 'none' : '';

  const isFullViewport = Array.from(owners.values()).some(
    (ownerOptions) => ownerOptions.fullViewport === true,
  );

  if (isFullViewport) {
    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'contain';
    body.style.overscrollBehavior = 'contain';
    documentElement.style.scrollbarGutter = 'auto';
    body.style.scrollbarGutter = 'auto';
    fullViewportApplied = true;
  } else if (fullViewportApplied) {
    documentElement.style.overflow = '';
    documentElement.style.overscrollBehavior = '';
    body.style.overscrollBehavior = '';
    documentElement.style.scrollbarGutter = '';
    body.style.scrollbarGutter = '';
    fullViewportApplied = false;
  }
}
